import React, { useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";
import { motion } from "framer-motion";
import {
  BarChart2,
  Activity,
  TrendingUp,
  PlusCircle,
  AlertTriangle
} from "lucide-react";
import Images from "../../../Images";
import CampaignList from "../../../components/Campaigns/CampaignList";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Config from "../../../Config";
import Loader from "../../../components/Loader";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const Campaigns = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user) || null;
  const subscription_details = user?.subscription;

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSubscriptionExpired = () => {
    if (!subscription_details?.end_date) return true;
    const endDate = new Date(subscription_details.end_date);
    const currentDate = new Date();
    return endDate < currentDate;
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(
        `${Config.backendDomain}vendor/vendorCampaigns`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
          },
        }
      );
      setCampaigns(response.data.data.data);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const redirectToCreateCampaignDeal = () => {
    if (isSubscriptionExpired()) {
      toast.error("Your subscription has expired. Please renew to create campaigns.", {
        icon: "⚠️",
      });
      navigate("/pricing");
      return;
    }
    navigate("/deal/campaigns/create");
  };

  return (
    <div>
      <Navbar page="Campaigns" />
      {loading ? (
        <Loader />
      ) : (
        <div className="min-h-screen bg-white">
          {isSubscriptionExpired() && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 mx-auto max-w-7xl"
            >
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">
                      Subscription Expired
                    </h3>
                    <p className="text-sm text-red-600">
                      Your subscription has expired. Please renew to continue using campaign features.
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/pricing")}
                    className="ml-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg shadow-lg transition-all duration-200 hover:bg-red-700"
                  >
                    Renew Subscription
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative bg-gradient-to-br from-[#FF6C6C] via-[#FF8F8F] to-[#FFA7A7] text-white overflow-hidden w-full"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.03, 0.08, 0.03],
                scale: [1, 1.2, 1],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%221%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
            />

            <div className="overflow-hidden absolute inset-0">
              {[BarChart2, Activity, TrendingUp].map((Icon, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{
                    opacity: [0.1, 0.3, 0.1],
                    y: [50, 30, 50],
                    x: index * 20,
                  }}
                  transition={{
                    duration: 3,
                    delay: index * 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute right-20"
                  style={{
                    top: `${20 + index * 15}%`,
                    right: `${10 + index * 5}%`,
                  }}
                >
                  <Icon size={24} className="text-white" />
                </motion.div>
              ))}
            </div>

            <div className="relative px-6 py-8 mx-auto max-w-7xl sm:px-8 lg:px-12">
              <div className="flex flex-col items-center space-y-4 md:flex-row md:space-x-8 md:space-y-0">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 20,
                    duration: 0.8,
                  }}
                  className="relative p-3 group"
                >
                  {/* Animated border */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r rounded-2xl from-white/30 to-white/10"
                    animate={{
                      background: [
                        "linear-gradient(0deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)",
                        "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)",
                        "linear-gradient(360deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)",
                      ],
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />

                  {/* Glass effect container */}
                  <div className="relative p-3 rounded-2xl backdrop-blur-lg bg-white/10">
                    <motion.img
                      src={Images.campaign_alt}
                      alt="campaign-overview"
                      className="object-contain w-24 h-24"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                  </div>
                </motion.div>

                <div className="relative w-full text-center md:text-left">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-2"
                  >
                    <div className="flex flex-col gap-4 w-full sm:flex-row sm:items-center">
                      <div className="inline-flex items-center space-x-2">
                        <h1 className="text-3xl font-bold tracking-tight">
                          Campaign List
                        </h1>
                        <motion.div
                          animate={{
                            scale: [1, 1.2, 1],
                            rotate: [0, 5, 0],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </motion.div>
                      </div>

                      <motion.button
                        onClick={redirectToCreateCampaignDeal}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center px-4 py-2 space-x-2 text-sm font-medium text-[#FF6C6C] bg-white shadow-lg transition-colors rounded-lg hover:bg-gray-50 sm:ml-auto z-10"
                      >
                        <PlusCircle size={18} />
                        <span>Add New Campaign</span>
                      </motion.button>
                    </div>
                    <p className="max-w-md text-sm font-medium text-white/90">
                      View your campaigns with detailed and perform actions like
                      Blast.
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.1, scale: 1 }}
              transition={{ duration: 1 }}
              className="overflow-hidden absolute top-0 right-0 -mt-16 -mr-16"
            >
              <div className="w-72 h-72 bg-gradient-to-br from-white to-transparent rounded-full via-white/50" />
            </motion.div>
          </motion.div>
          <CampaignList campaigns={campaigns} fetchCampaigns={fetchCampaigns} />
        </div>
      )}
    </div>
  );
};

export default Campaigns;
